import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../storeTypes';
import { ActivityDefinition, ActivityDefinitionId } from '../../types/library';
import { customVersionToActivityDefinition } from '../../types/mappers/customActivityMapper';
import { getActivityDefinitionsSelector } from './librarySelectors';

export const getCustomActivitiesSelector = (state: RootState) =>
  state.customActivitiesState.customActivities;

// All non-archived custom activities (the My Activities default view).
export const getActiveCustomActivitiesSelector = createSelector(
  [getCustomActivitiesSelector],
  (customActivities) => Object.values(customActivities).filter((ca) => !ca.is_archived),
);

// Archived custom activities (the "Show archived" view in My Activities).
export const getArchivedCustomActivitiesSelector = createSelector(
  [getCustomActivitiesSelector],
  (customActivities) => Object.values(customActivities).filter((ca) => ca.is_archived),
);

export const getCustomActivityByIdSelector = (id: string) => (state: RootState) =>
  state.customActivitiesState.customActivities[id];

// Every custom-activity VERSION (all versions, not just current) projected to the portal's
// ActivityDefinition shape, keyed by movement_id. Merged into the standard library definitions
// map at submit time so toMovementInput (treatmentPlanMapper) doesn't throw on a custom id — and
// so a plan/template that bound an OLDER version still resolves.
export const getCustomActivityDefinitionsByMovementIdSelector = createSelector(
  [getCustomActivitiesSelector],
  (customActivities): Record<ActivityDefinitionId, ActivityDefinition> => {
    const map: Record<ActivityDefinitionId, ActivityDefinition> = {};
    Object.values(customActivities).forEach((ca) => {
      ca.versions.forEach((version) => {
        map[version.movement_id] = customVersionToActivityDefinition(version);
      });
    });
    return map;
  },
);

// Standard library + custom-activity versions, keyed by id. Use this anywhere that resolves a
// plan/template/results activity definition by id — a plan or template item may bind a custom
// version, which is absent from the standard library map. Resolving against only the standard map
// renders "cannot display this activity" or throws in validation.
export const getAllActivityDefinitionsSelector = createSelector(
  [getActivityDefinitionsSelector, getCustomActivityDefinitionsByMovementIdSelector],
  (standard, custom): Record<ActivityDefinitionId, ActivityDefinition> => ({
    ...standard,
    ...custom,
  }),
);

// ---- Config catalog (metric ranges + param-option lists from AlgoConfig CSVs) ----
// The editor's dropdowns + validation read these instead of hardcoded constants. Selectors return
// empty defaults until the catalog is fetched (fetchConfigCatalogAction), so callers render nothing
// rather than crash during load.

export const getConfigCatalogSelector = (state: RootState) =>
  state.customActivitiesState.configCatalog;

const paramOptions =
  (slug: string) =>
  (state: RootState): string[] =>
    state.customActivitiesState.configCatalog?.param_options?.[slug] ?? [];

export const getPositionOptionsSelector = paramOptions('position');
export const getPlaneOptionsSelector = paramOptions('plane');
export const getBodyPartOptionsSelector = paramOptions('body_parts');
export const getCategoryOptionsSelector = paramOptions('category');
export const getMuscleGroupOptionsSelector = paramOptions('musclegroups');
export const getEquipmentOptionsSelector = paramOptions('equipment');
// The 14 body keypoints selectable for two-joints rep counting.
export const getTwoJointsOptionsSelector = paramOptions('joints_for_rep_on_two_joints');

// Rep-counting angles: the curated param_options list if present, else derived from the metrics
// flagged is_rep_angle (belt-and-suspenders — both come from the same CSVs).
export const getRepOnAngleOptionsSelector = createSelector(
  [getConfigCatalogSelector],
  (catalog): string[] => {
    if (!catalog) {
      return [];
    }
    const fromParams = catalog.param_options?.reps_on_angle;
    if (fromParams && fromParams.length > 0) {
      return fromParams;
    }
    return catalog.metrics.filter((m) => m.is_rep_angle).map((m) => m.name);
  },
);

// Measured (post-exercise) angle options — the rep-on angle set.
export const getPostMetricOptionsSelector = getRepOnAngleOptionsSelector;

// Metric names that are BILATERAL (measured on both limbs) — catalog max_number_per_body === 2.
// The editor annotates these as "…, both sides" and the cloud expands them to target+other.
export const getBilateralMetricNamesSelector = createSelector(
  [getConfigCatalogSelector],
  (catalog): ReadonlySet<string> =>
    new Set(
      (catalog?.metrics ?? []).filter((m) => m.max_number_per_body === 2).map((m) => m.name),
    ),
);

// metric name -> [min, max] degrees, for initial-angle validation. Numbers parsed from the catalog.
export const getAngleRangesSelector = createSelector(
  [getConfigCatalogSelector],
  (catalog): Record<string, [number, number]> => {
    const ranges: Record<string, [number, number]> = {};
    catalog?.metrics.forEach((m) => {
      const min = Number(m.min);
      const max = Number(m.max);
      if (!Number.isNaN(min) && !Number.isNaN(max)) {
        ranges[m.name] = [min, max];
      }
    });
    return ranges;
  },
);
